import React from 'react';
import { Box, Label, spacing } from '@apia/theme';
import MakeField from '../fieldsStore/MakeField';
import { IApiaCheckBox } from '../../../../types';
import useContext from '../../../../hooks/useContext';
import { ApiaComponentContextPrimitive } from '../apiaComponent/ApiaComponentContext';
import FieldContext from '../fieldsStore/FieldContext';
import useApiaFieldCommonProps from './useApiaFieldCommonProps';
import Checkbox from '../../../common/fields/Checkbox';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import { useApiaFormContext } from '../page/ApiaForm';
import RequiredBox from '../common/RequiredBox';
import IProperty from '../../../../JSApi/constants/IProperty';

const ApiaCheckboxRenderer = React.forwardRef<HTMLButtonElement>((_, ref) => {
  const {
    state: { value },
    setValue,
    fireEvent,
  } = useContext(FieldContext);
  const { field, fieldId, onGrid } = useContext(ApiaComponentContextPrimitive);
  const props = usePropsSelector(fieldId);

  const { attLabel, attName } = field as IApiaCheckBox;

  const { isReadonly, identifier } = useApiaFieldCommonProps();
  const { isFormReadOnly } = useApiaFormContext();
  return (
    <>
      {!props.onGrid && !props.attributes.onGrid && (
        <Box as="span" className="spacer">
          &nbsp;
        </Box>
      )}
      <Label
        id={`lbl_${fieldId}`}
        key={`lbl_${fieldId}_${attLabel}`}
        title={props.tooltip}
        htmlFor={fieldId}
        className="checkbox__field"
        {...window.getVariant('forms.checkbox')}
      >
        <Checkbox
          portalId={`portal${identifier.id}`}
          ref={ref}
          key={`${fieldId}_${props.name ?? ''}`}
          name={props.name || attName}
          aria-label={
            props[IProperty.PROPERTY_ARIA_LABEL] ||
            props.tooltip ||
            attLabel ||
            attName
          }
          autoFocus={
            props[IProperty.PROPERTY_AUTO_FOCUS] === 'true' ? true : undefined
          }
          aria-description={props[IProperty.PROPERTY_ARIA_DESCRIPTION]}
          title={props[IProperty.PROPERTY_TITLE] || props.tooltip || attLabel}
          disabled={
            props.disabled ||
            isFormReadOnly ||
            props[IProperty.PROPERTY_INPUT_AS_TEXT]
          }
          checked={value as boolean}
          data-required={props.required}
          onChange={(ev) => {
            if (isReadonly || isFormReadOnly) return;
            setValue(ev.target.checked);
            fireEvent('onChange');
          }}
          inputProps={identifier}
          required={props.required}
        />

        {!onGrid && (
          <>
            <Box
              as="span"
              id={`chk_lbl_${fieldId}`}
              sx={{
                pl: spacing(3),
                mr: '2',
                fontWeight: 'body',
                color: props.disabled ? 'muted' : props.fontColor,
              }}
            >
              <RequiredBox
                label={attLabel}
                avoidColon
                required={props.required ?? false}
                isFormReadonly={
                  isFormReadOnly || props[IProperty.PROPERTY_INPUT_AS_TEXT]
                }
              />
            </Box>
          </>
        )}
      </Label>
      <span id={`portal${identifier.id}`}></span>
    </>
  );
});

const ApiaCheckbox = MakeField(ApiaCheckboxRenderer, {
  doScriptEvents: async ({ fireScriptEvents }) => {
    let hasFiredScriptEventsCorrectly = await fireScriptEvents('onClick');
    if (!hasFiredScriptEventsCorrectly) return false;

    hasFiredScriptEventsCorrectly = await fireScriptEvents('onChange');
    return hasFiredScriptEventsCorrectly;
  },
  doServerEvents: ({ fieldContext, fireServerEvents }) => {
    const events = fieldContext.componentContext.field.serverEvents;
    if (events) {
      if (events.find((searchEvent) => searchEvent.evtName === 'onChange'))
        return fireServerEvents('onChange');
      return fireServerEvents('onClick');
    }
    return true;
  },
  getInitialValue({
    context: {
      field: {
        value,
        properties: { checked },
      },
    },
  }) {
    return value === true || (value === '' && (checked || false));
  },
});

export default ApiaCheckbox;
